import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { AnalysisService } from './analysis.service';
import { ChatNotificationWsService } from './chat-notification-ws.service';
import { AnalysisChatStateService } from './analysis-chat-state.service';
import { SessionService } from '@core/auth/services/session.service';
import { ClientChatSummary } from '@core/models/chat.models';

const ACTIVE_STATES: string[] = ['esperando consentimiento', 'activo'];

@Injectable({
  providedIn: 'root'
})
export class ChatManagerService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private initialized = false;
  private clienteId: string | null = null;

  private chatsSubject = new BehaviorSubject<ClientChatSummary[]>([]);
  private activeChatsSubject = new BehaviorSubject<ClientChatSummary[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);

  chats$ = this.chatsSubject.asObservable();
  activeChats$ = this.activeChatsSubject.asObservable();
  loading$ = this.loadingSubject.asObservable();

  constructor(
    private analysisService: AnalysisService,
    private chatWs: ChatNotificationWsService,
    private chatStateService: AnalysisChatStateService,
    private sessionService: SessionService
  ) {}

  initialize(): void {
    if (this.initialized) return;
    this.initialized = true;

    const currentUser = this.sessionService.getCurrentUser();
    this.clienteId = currentUser?.profile?.user_id || currentUser?.id || null;

    console.log('[ChatManager] initialize - clienteId:', this.clienteId);

    if (!this.clienteId) return;

    this.chatWs.connect();

    this.listenNewChats();

    this.loadClientChats();
  }

  private loadClientChats(): void {
    this.loadingSubject.next(true);

    this.analysisService.getClientChats().subscribe({
      next: (chats) => {
        console.log('[ChatManager] chats recibidos:', chats.length, chats);
        this.chatsSubject.next(chats);
        const active = chats.filter(c => ACTIVE_STATES.includes(c.estadoNombre.toLowerCase()));
        console.log('[ChatManager] chats activos:', active.length, active);
        this.activeChatsSubject.next(active);

        active.forEach(chat => this.chatWs.subscribeToChat(chat.chatId));

        this.loadingSubject.next(false);
      },
      error: (err) => {
        console.error('[ChatManager] error cargando chats:', err);
        this.loadingSubject.next(false);
      }
    });
  }

  private listenNewChats(): void {
    this.chatWs.newChat$
      .pipe(takeUntil(this.destroy$))
      .subscribe(notification => {
        const newChat: ClientChatSummary = {
          chatId: notification.chatId,
          solicitudId: notification.solicitudId,
          assistantName: '',
          lawyerDisplay: '',
          estadoId: '',
          estadoNombre: 'Activo',
          completenessScore: 0,
          questionsAsked: 0,
          questionsAnswered: 0,
          lastMessageAt: null,
          fechaCreacion: new Date().toISOString()
        };

        const currentChats = this.chatsSubject.value;
        const exists = currentChats.some(c => c.chatId === notification.chatId);
        if (!exists) {
          this.chatsSubject.next([newChat, ...currentChats]);
        }

        const currentActive = this.activeChatsSubject.value;
        const activeExists = currentActive.some(c => c.chatId === notification.chatId);
        if (!activeExists) {
          this.activeChatsSubject.next([newChat, ...currentActive]);
        }

        this.chatWs.subscribeToChat(notification.chatId);

        if (this.clienteId) {
          this.chatStateService.openChat(notification.solicitudId, this.clienteId, notification.chatId);
        }
      });
  }

  openChat(chat: ClientChatSummary): void {
    if (!this.clienteId) return;
    this.chatStateService.openChat(chat.solicitudId, this.clienteId, chat.chatId);
  }

  refreshChats(): void {
    this.loadClientChats();
  }

  /**
   * Refresca la lista de chats con reintentos hasta encontrar el chat
   * de la solicitud indicada, y lo abre automáticamente.
   */
  refreshAndOpenChat(solicitudId: string, delayMs = 5000, maxRetries = 3): void {
    if (!this.clienteId) return;

    let attempt = 0;
    const tryLoad = () => {
      attempt++;
      console.log(`[ChatManager] refreshAndOpenChat intento ${attempt} para solicitud ${solicitudId}`);

      this.analysisService.getClientChats().subscribe({
        next: (chats) => {
          this.chatsSubject.next(chats);
          const active = chats.filter(c => ACTIVE_STATES.includes(c.estadoNombre.toLowerCase()));
          this.activeChatsSubject.next(active);
          active.forEach(chat => this.chatWs.subscribeToChat(chat.chatId));

          const target = active.find(c => c.solicitudId === solicitudId);
          if (target && this.clienteId) {
            console.log('[ChatManager] chat encontrado, abriendo:', target.chatId);
            this.chatStateService.openChat(target.solicitudId, this.clienteId, target.chatId);
          } else if (attempt < maxRetries) {
            setTimeout(tryLoad, delayMs);
          }
        },
        error: () => {
          if (attempt < maxRetries) {
            setTimeout(tryLoad, delayMs);
          }
        }
      });
    };

    setTimeout(tryLoad, delayMs);
  }

  get activeCount(): number {
    return this.activeChatsSubject.value.length;
  }

  teardown(): void {
    this.chatWs.disconnect();
    this.chatsSubject.next([]);
    this.activeChatsSubject.next([]);
    this.initialized = false;
    this.clienteId = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.teardown();
  }
}
